import { useMemo } from 'react';
import { graphql } from 'gatsby';
import Author from './components/Author/Author';
import BlogTag from '@/components/BlogTag/BlogTag';
import {
  StyledSection,
  StyledContainer,
  StyledHeadlineContainer,
  StyledEyebrow,
  StyledHeadline,
  StyledInfoContainer,
  StyledTag,
  StyledImageElement,
  StyledArticleInfo,
  StyledFigure,
  StyledDate,
  StyledImagWrap,
  StyledTagsList,
} from './HeroBlockArticleDetail2.styles';
import StructuredData from '@/components/StructuredData/StructuredData';
import dateForSchema from '@/utils/format/dateForSchema';
import { type BlockHTMLId } from '@/utils/renderBlock';

export const HERO_IMAGE_HEIGHT = 586;
export const HERO_IMAGE_WIDTH = 1120;

interface HeroBlockArticleDetail2Props extends Queries.HERO_BLOCK_ARTICLE_DETAIL_2Fragment {
  tags?: Queries.BLOG_TAGFragment[];
}

const HeroBlockArticleDetail2 = ({
  heroBlockArticleDetail2Elements,
  tags = [],
  system,
  id,
}: HeroBlockArticleDetail2Props & BlockHTMLId) => {
  const { eyebrow, title, author_information, image, estimated_reading_time, date } =
    heroBlockArticleDetail2Elements;

  const hasEyebrow = eyebrow?.value;
  const articleTitle = title.value;
  const authorValue = author_information.value;
  const optionalHeroImage = {
    ...image?.value,
    isFilledIn: !!image?.value?.url,
    url: image?.value?.url || '',
    description: image?.value?.description || '',
    height: image?.value?.height || HERO_IMAGE_HEIGHT,
    width: image?.value?.width || HERO_IMAGE_WIDTH,
  };

  const estimatedReadingTime = estimated_reading_time?.value;
  const showReadingTime = estimatedReadingTime && estimatedReadingTime > 1;
  const publicationDateFormatted = date?.formatted;
  const publicationDateRaw = date?.raw as string;

  const structuredData = useMemo(() => {
    const schema = {
      '@context': 'https://schema.org',
      '@type': 'Article',
      name: title.value,
      author: {
        '@type': 'Person',
        name: authorValue.authorElements.name.value,
      },
      datePublished: dateForSchema(publicationDateRaw),
      ...(optionalHeroImage.url ? { image: optionalHeroImage.url } : {}),
    };

    return schema;
  }, [
    title.value,
    authorValue.authorElements.name.value,
    optionalHeroImage.url,
    publicationDateRaw,
  ]);

  return (
    <StyledSection
      data-codename={system.codename}
      hasHeroImage={optionalHeroImage.isFilledIn}
      id={id}
    >
      <StructuredData schema={structuredData} />
      <StyledContainer>
        <StyledHeadlineContainer>
          {hasEyebrow && <StyledEyebrow>{eyebrow.value}</StyledEyebrow>}
          <StyledHeadline>{articleTitle}</StyledHeadline>
        </StyledHeadlineContainer>
        <StyledInfoContainer>
          <Author {...authorValue} />
          <StyledArticleInfo>
            <StyledDate dateTime={publicationDateRaw}>{publicationDateFormatted}</StyledDate>
            <StyledTag>{showReadingTime ? `${estimatedReadingTime} min read` : `Date`}</StyledTag>
          </StyledArticleInfo>
        </StyledInfoContainer>
        {tags && tags.length > 0 && (
          <StyledTagsList>
            {tags.map((tag) => (
              <li key={tag.system.id}>
                <BlogTag {...tag} />
              </li>
            ))}
          </StyledTagsList>
        )}
      </StyledContainer>
      {optionalHeroImage.isFilledIn && (
        <StyledImagWrap>
          <StyledContainer>
            <StyledFigure>
              <StyledImageElement
                alt={optionalHeroImage.description}
                height={optionalHeroImage.height}
                loading="eager"
                image={{ ...optionalHeroImage, image_id: optionalHeroImage.url }}
                width={optionalHeroImage.width}
              />
            </StyledFigure>
          </StyledContainer>
        </StyledImagWrap>
      )}
    </StyledSection>
  );
};

export const fragment = graphql`
  fragment HERO_BLOCK_ARTICLE_DETAIL_2 on kontent_item_hero_block_article_detail_2 {
    __typename
    system {
      id
      type
      codename
    }
    heroBlockArticleDetail2Elements: value {
      ...COMPONENT_A_B_TEST
      author_information {
        value {
          ... on kontent_item_author {
            ...AUTHOR
          }
        }
      }
      eyebrow {
        value
      }
      image {
        value {
          width
          url
          type
          height
          description
        }
      }
      title {
        value
      }
      estimated_reading_time {
        value
      }
      date {
        formatted: value(formatString: "MMMM D, YYYY")
        raw: value
      }
    }
  }
`;

export default HeroBlockArticleDetail2;
